import { useRef, useState, useEffect } from 'react';
import type { ReleaseDecision, ReleaseInput, StructuredRecommendation, RecommendationPriority, ContextInsight, InsightType, SuggestedTestCase, TestCaseStatus } from '../types';
import { useLanguage, formatDate } from '../i18n';
import { analytics } from '../analytics';
import RatingModal, { shouldShowRatingModal, resetSkipUntilNext } from './RatingModal';
import MaxAIReport from './MaxAIReport';
import { downloadPdfReport, getQuotaInfo, type QuotaInfo } from '../api';
import { useAuth } from '../auth';
import GitHubReportBlock, { type GithubContextForReport } from './GitHubReportBlock';
import { stripBullet } from '../utils';

const LAST_GITHUB_CONTEXT_KEY = 'releasegate_last_github_context';

// AI 제안 테스트 케이스 결과 컴포넌트
function SuggestedTestcasesResult({ testcases, lang }: { testcases: SuggestedTestCase[]; lang: 'ko' | 'en' }) {
  const statusConfig: Record<TestCaseStatus, { icon: string; color: string; label: string }> = {
    'pass': { icon: '✓', color: 'text-status-go', label: lang === 'ko' ? '통과' : 'Pass' },
    'fail': { icon: '✗', color: 'text-status-danger', label: lang === 'ko' ? '실패' : 'Fail' },
    'n/a': { icon: '−', color: 'text-terminal-muted', label: lang === 'ko' ? '해당없음' : 'N/A' },
    'n/t': { icon: '○', color: 'text-status-warning', label: lang === 'ko' ? '미테스트' : 'Not Tested' },
  };

  const priorityConfig: Record<string, { color: string; label: string }> = {
    'high': { color: 'text-status-danger', label: lang === 'ko' ? '높음' : 'High' },
    'medium': { color: 'text-status-warning', label: lang === 'ko' ? '보통' : 'Medium' },
    'low': { color: 'text-terminal-muted', label: lang === 'ko' ? '낮음' : 'Low' },
  };

  const passCount = testcases.filter(tc => tc.status === 'pass').length;
  const failCount = testcases.filter(tc => tc.status === 'fail').length;
  const ntCount = testcases.filter(tc => tc.status === 'n/t').length;
  const naCount = testcases.filter(tc => tc.status === 'n/a').length;

  return (
    <div className="mt-4">
      <div className="text-terminal-dim text-xs mb-2 flex items-center gap-2">
        <span>💡</span>
        <span>{lang === 'ko' ? 'AI 제안 테스트 케이스 결과' : 'AI Suggested Test Case Results'}</span>
      </div>
      
      {/* 요약 */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <span className="text-status-go">✓ {passCount} {lang === 'ko' ? '통과' : 'Pass'}</span>
        <span className="text-status-danger">✗ {failCount} {lang === 'ko' ? '실패' : 'Fail'}</span>
        <span className="text-status-warning">○ {ntCount} {lang === 'ko' ? '미테스트' : 'Not Tested'}</span>
        <span className="text-terminal-muted">− {naCount} {lang === 'ko' ? '해당없음' : 'N/A'}</span>
      </div>

      {/* 테스트 케이스 목록 */}
      <div className="space-y-2">
        {testcases.map((tc) => {
          const status = statusConfig[tc.status];
          const priority = priorityConfig[tc.priority] || priorityConfig['medium'];
          
          return (
            <div key={tc.id} className="bg-surface-elevated p-2 rounded border border-border-subtle">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="text-terminal-muted font-mono">{tc.id}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className="text-terminal-muted">{tc.category}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className={priority.color}>{priority.label}</span>
                  </div>
                  <p className="text-sm text-terminal-text truncate">{tc.title || tc.description}</p>
                  {tc.title && tc.description && tc.title !== tc.description && (
                    <p className="text-xs text-terminal-muted mt-1">{tc.description}</p>
                  )}
                  
                  {/* GitHub 커밋 정보 표시 */}
                  {tc.github_commit_sha && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      <span className="px-1.5 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-purple-400 font-mono">
                        🔗 {lang === 'ko' ? '커밋' : 'Commit'}: {tc.github_commit_sha.substring(0, 7)}
                      </span>
                      {tc.github_repo && (
                        <span className="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-blue-400">
                          📦 {tc.github_repo}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 담당자 정보 표시 */}
                  {(tc.author || tc.tester || tc.modifier) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      {tc.author && (
                        <span className="px-1.5 py-0.5 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400">
                          👤 {lang === 'ko' ? '작성자' : 'Author'}: {tc.author}
                        </span>
                      )}
                      {tc.tester && (
                        <span className="px-1.5 py-0.5 bg-green-500/10 border border-green-500/30 rounded text-green-400">
                          🧪 {lang === 'ko' ? '진행자' : 'Tester'}: {tc.tester}
                        </span>
                      )}
                      {tc.modifier && (
                        <span className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400">
                          ✏️ {lang === 'ko' ? '수정자' : 'Modifier'}: {tc.modifier}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 메모 표시 */}
                  {tc.memo && (
                    <div className="mt-2 p-2 bg-terminal-bg rounded border border-terminal-border">
                      <div className="flex items-center gap-1 text-xs text-terminal-dim mb-1">
                        <span>📝</span>
                        <span>{lang === 'ko' ? '메모' : 'Memo'}</span>
                      </div>
                      <p className="text-xs text-terminal-muted whitespace-pre-wrap">{tc.memo}</p>
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${status.color} bg-surface-overlay text-xs whitespace-nowrap`}>
                  <span>{status.icon}</span>
                  <span>{status.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ResultBoardProps {
  result: ReleaseDecision;
  inputData: ReleaseInput | null;
  onReset: () => void;
  isLoading?: boolean;
  isDemo?: boolean;  // 데모 결과인지 여부 (워터마크 표시용)
}

function ResultBoard({ result, inputData, onReset, isLoading = false, isDemo = false }: ResultBoardProps) {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const reportRef = useRef<HTMLDivElement>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [githubCtx, setGithubCtx] = useState<GithubContextForReport | null>(null);

  // Quota 정보 가져오기
  useEffect(() => {
    if (user) {
      getQuotaInfo()
        .then(setQuotaInfo)
        .catch(console.error);
    }
  }, [user]);

  // GitHub 컨텍스트 로드 (폼에서 localStorage에 저장)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_GITHUB_CONTEXT_KEY);
      if (!raw) {
        setGithubCtx(null);
        return;
      }
      setGithubCtx(JSON.parse(raw));
    } catch {
      setGithubCtx(null);
    }
  }, [result.feature_name, result.release_date]);

  const canDownloadPdf = quotaInfo?.limits.pdf_download ?? false;

  const statusConfig = {
    go: { label: t.status.go, badge: 'badge-go', color: 'text-status-go', desc: t.status.goDesc },
    conditional: { label: t.status.conditional, badge: 'badge-conditional', color: 'text-status-warning', desc: t.status.conditionalDesc },
    holding: { label: t.status.holding, badge: 'badge-holding', color: 'text-status-holding', desc: t.status.holdingDesc },
    no_go: { label: t.status.noGo, badge: 'badge-nogo', color: 'text-status-danger', desc: t.status.noGoDesc },
  };

  const config = statusConfig[result.status];

  const getBarColor = (score: number, max: number) => {
    const pct = (score / max) * 100;
    if (pct >= 70) return 'bg-status-go';
    if (pct >= 40) return 'bg-status-warning';
    return 'bg-status-danger';
  };

  const getScoreGrade = (score: number) => {
    if (score >= 80) return { grade: 'A', desc: t.grade.excellent };
    if (score >= 70) return { grade: 'B', desc: t.grade.good };
    if (score >= 60) return { grade: 'C', desc: t.grade.average };
    if (score >= 50) return { grade: 'D', desc: t.grade.poor };
    return { grade: 'F', desc: t.grade.insufficient };
  };

  const scoreGrade = getScoreGrade(result.confidence_score);
  const now = formatDate(new Date(), lang);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [showMaxReport, setShowMaxReport] = useState(false);

  // PDF 생성 중에는 화면 스크롤/상호작용을 막기 위해 body 스크롤 잠금
  useEffect(() => {
    if (!isGenerating) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isGenerating]);

  // 별점 제출 토스트 자동 숨김
  useEffect(() => {
    if (!ratingSubmitted) return;
    const timer = setTimeout(() => setRatingSubmitted(false), 3000);
    return () => clearTimeout(timer);
  }, [ratingSubmitted]);
  
  // 플랜 여부 확인 (quotaInfo.plan은 DevPlanSwitcher 오버라이드가 적용됨)
  const effectivePlan = quotaInfo?.plan;
  const isMaxPlan = effectivePlan === 'max';
  const isTeamPlan = effectivePlan === 'team';
  const isProPlan = effectivePlan === 'pro';
  
  // AI 리포트 사용 가능 여부 (Max 또는 Team 플랜)
  const canUseAIReport = isMaxPlan || isTeamPlan;

  // GA4: 결과 확인 이벤트 (컴포넌트 마운트 시)
  // + "나중에" 스킵 상태 리셋 + 별점 모달 표시 여부 확인
  useEffect(() => {
    analytics.resultView(result.status);
    
    // 비로그인 사용자(데모 결과)는 별점 모달 표시하지 않음
    if (!user) {
      return;
    }
    
    // "나중에"를 눌렀다면 이번 결과에서 리셋
    resetSkipUntilNext();
    
    // 약간의 딜레이 후 별점 모달 표시 (사용자가 결과를 잠깐 볼 시간을 줌)
    const timer = setTimeout(() => {
      if (shouldShowRatingModal()) {
        setShowRatingModal(true);
      }
    }, 2000); // 2초 후 표시
    
    return () => clearTimeout(timer);
  }, [result.status, user]);

  // 우선순위별 색상 및 라벨
  const getPriorityConfig = (priority: RecommendationPriority) => {
    const configs = {
      high: { 
        color: 'text-status-danger', 
        bg: 'bg-status-danger/10 border-status-danger/30',
        label: t.result.priorityHigh,
        icon: '🔴'
      },
      medium: { 
        color: 'text-status-warning', 
        bg: 'bg-status-warning/10 border-status-warning/30',
        label: t.result.priorityMedium,
        icon: '🟡'
      },
      low: { 
        color: 'text-status-go', 
        bg: 'bg-status-go/10 border-status-go/30',
        label: t.result.priorityLow,
        icon: '🟢'
      },
    };
    return configs[priority];
  };

  // 인사이트 타입별 설정
  const getInsightConfig = (insightType: InsightType, keyword: string) => {
    // 키워드별 특수 설정
    const keywordLower = keyword.toLowerCase();
    
    // 플랫폼
    if (keywordLower.includes('ios') || keywordLower.includes('iphone')) {
      return { icon: '🍎', color: 'text-blue-500', borderColor: 'border-blue-500', bgColor: 'bg-blue-500/10' };
    }
    if (keywordLower.includes('android')) {
      return { icon: '🤖', color: 'text-green-500', borderColor: 'border-green-500', bgColor: 'bg-green-500/10' };
    }
    if (keywordLower.includes('web')) {
      return { icon: '🌐', color: 'text-purple-500', borderColor: 'border-purple-500', bgColor: 'bg-purple-500/10' };
    }
    
    // 기술 스택
    if (keywordLower.includes('webrtc')) {
      return { icon: '📹', color: 'text-orange-500', borderColor: 'border-orange-500', bgColor: 'bg-orange-500/10' };
    }
    if (keywordLower.includes('graphql')) {
      return { icon: '◈', color: 'text-pink-500', borderColor: 'border-pink-500', bgColor: 'bg-pink-500/10' };
    }
    if (keywordLower.includes('kafka')) {
      return { icon: '📨', color: 'text-gray-600', borderColor: 'border-gray-600', bgColor: 'bg-gray-500/10' };
    }
    if (keywordLower.includes('firebase')) {
      return { icon: '🔥', color: 'text-amber-500', borderColor: 'border-amber-500', bgColor: 'bg-amber-500/10' };
    }
    
    // 서비스
    if (keywordLower.includes('카카오') || keywordLower.includes('kakao')) {
      return { icon: '💬', color: 'text-yellow-600', borderColor: 'border-yellow-500', bgColor: 'bg-yellow-500/10' };
    }
    if (keywordLower.includes('네이버') || keywordLower.includes('naver')) {
      return { icon: '🟢', color: 'text-green-600', borderColor: 'border-green-600', bgColor: 'bg-green-500/10' };
    }
    if (keywordLower.includes('토스') || keywordLower.includes('toss')) {
      return { icon: '💳', color: 'text-blue-600', borderColor: 'border-blue-600', bgColor: 'bg-blue-500/10' };
    }
    
    // 타입별 기본 설정
    const typeConfigs: Record<InsightType, { icon: string; color: string; borderColor: string; bgColor: string }> = {
      platform: { icon: '📱', color: 'text-blue-500', borderColor: 'border-blue-500', bgColor: 'bg-blue-500/10' },
      technology: { icon: '⚙️', color: 'text-purple-500', borderColor: 'border-purple-500', bgColor: 'bg-purple-500/10' },
      service: { icon: '🔗', color: 'text-amber-500', borderColor: 'border-amber-500', bgColor: 'bg-amber-500/10' },
      domain: { icon: '🏢', color: 'text-emerald-500', borderColor: 'border-emerald-500', bgColor: 'bg-emerald-500/10' },
    };
    return typeConfigs[insightType] || typeConfigs.platform;
  };

  // 인사이트 타입 라벨
  const getInsightTypeLabel = (insightType: InsightType) => {
    const labels = {
      platform: lang === 'ko' ? '플랫폼' : 'Platform',
      technology: lang === 'ko' ? '기술' : 'Tech',
      service: lang === 'ko' ? '서비스' : 'Service',
      domain: lang === 'ko' ? '도메인' : 'Domain',
    };
    return labels[insightType] || insightType;
  };

  // 상태에 따른 파일명 접미사
  const getStatusSuffix = (status: string) => {
    switch (status) {
      case 'go':
        return 'GO';
      case 'no_go':
        return 'NoGo';
      case 'conditional':
        return 'Conditional';
      default:
        return status;
    }
  };

  const handleDownloadPDF = async () => {
    if (isGenerating) return;
    
    // 로그인 체크
    if (!user) {
      setPdfError(t.auth.loginRequired);
      return;
    }
    
    // 플랜 체크
    if (!canDownloadPdf) {
      setPdfError(t.result.pdfUpgradeHint);
      return;
    }
    
    setIsGenerating(true);
    setPdfError(null);
    
    try {
      // 서버사이드 PDF 생성
      const resultData = {
        feature_name: result.feature_name,
        release_date: result.release_date,
        confidence_score: result.confidence_score,
        status: result.status,
        status_label: result.status_label,
        breakdown: result.breakdown,
        blockers: result.blockers,
        recommendations: result.recommendations,
        structured_recommendations: result.structured_recommendations,
      };
      
      const inputDataForPdf = inputData ? {
        feature_name: inputData.feature_name,
        release_date: inputData.release_date,
        planning_intent: inputData.planning_intent,
        risks: inputData.risks,
        qa_status: inputData.qa_status,
        lang: lang,
      } : undefined;
      
      const blob = await downloadPdfReport(resultData, inputDataForPdf, lang, (githubCtx as unknown as Record<string, unknown>) ?? null);
      
      // 파일명 생성: feature_name-상태.pdf
      const sanitizedName = result.feature_name.replace(/[^a-zA-Z0-9가-힣_\- ]/g, '').replace(/\s+/g, '_');
      const suffix = getStatusSuffix(result.status);
      const fileName = `${sanitizedName}-${suffix}.pdf`;
      
      // Blob을 다운로드
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // GA4: PDF 다운로드 이벤트
      analytics.pdfDownload(result.status);
    } catch (error) {
      console.error('PDF 생성 오류:', error);
      
      // 서버 에러 시 클라이언트 PDF로 폴백
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('503') || errorMessage.includes('일시적으로')) {
        console.log('서버 PDF 불가, 클라이언트 PDF로 폴백...');
        setIsGenerating(false);
        await handleDownloadPDFClient();
        return;
      }
      
      setPdfError(t.result.pdfError);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // 클라이언트사이드 PDF (Free 플랜 폴백 - 기본 기능)
  const handleDownloadPDFClient = async () => {
    if (!reportRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = reportRef.current;
      
      // 파일명 생성: feature_name-상태.pdf
      const sanitizedName = result.feature_name.replace(/[^a-zA-Z0-9가-힣_\- ]/g, '').replace(/\s+/g, '_');
      const suffix = getStatusSuffix(result.status);
      const fileName = `${sanitizedName}-${suffix}.pdf`;
      
      // CDN에서 로드된 html2pdf 사용
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const html2pdf = (window as any).html2pdf;
      
      if (!html2pdf) {
        console.error('html2pdf가 로드되지 않았습니다.');
        return;
      }
      
      // PDF 출력용 스타일 적용
      element.classList.add('pdf-export');
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollY: 0,
          windowHeight: element.scrollHeight,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // GA4: PDF 다운로드 이벤트
      analytics.pdfDownload(result.status);
      
      // 스타일 복원
      element.classList.remove('pdf-export');
    } catch (error) {
      console.error('PDF 생성 오류:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // 별점 제출 핸들러
  const handleRatingSubmit = (rating: number, feedback?: string) => {
    console.log('Rating submitted:', rating, feedback);
    setShowRatingModal(false);
    setRatingSubmitted(true);
  };

  // 별점 모달 스킵 핸들러
  const handleRatingSkip = () => {
    setShowRatingModal(false);
  };

  return (
    <div className="space-y-4 relative" data-testid="result-board">
      {/* 별점 평가 모달 */}
      {showRatingModal && (
        <RatingModal
          onClose={() => setShowRatingModal(false)}
          onSubmit={handleRatingSubmit}
          onSkip={handleRatingSkip}
        />
      )}
      
      {/* MAX AI 리포트 모달 */}
      <MaxAIReport
        result={result}
        inputData={inputData}
        isOpen={showMaxReport}
        onClose={() => setShowMaxReport(false)}
        createdAt={new Date().toISOString()}
        createdWithPlan={quotaInfo?.plan || user?.subscription_plan}
      />

      {/* 별점 제출 완료 토스트 */}
      {ratingSubmitted && (
        <div className="fixed bottom-4 right-4 bg-status-go text-terminal-bg px-4 py-3 rounded-lg shadow-lg z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center gap-2">
            <span>✨</span>
            <span className="text-sm font-medium">{t.rating.thankYouComplete}</span>
          </div>
        </div>
      )}

      {/* 로딩 오버레이 */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="loading-overlay">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
            <div className="text-terminal-text text-sm font-mono">
              {lang === 'ko' ? '언어 변경 중...' : 'Changing language...'}
            </div>
          </div>
        </div>
      )}

      {/* PDF 생성 오버레이 (전체 디밍) */}
      {isGenerating && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center"
          data-testid="pdf-generating-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-4 px-6 py-5 bg-terminal-bg/80 border border-terminal-border rounded-xl shadow-2xl">
            <div className="w-12 h-12 border-4 border-status-warning/30 border-t-status-warning rounded-full animate-spin" />
            <div className="text-terminal-text text-sm font-semibold">
              {user && canDownloadPdf ? t.result.pdfDownloading : t.result.generating}
            </div>
            <div className="text-terminal-muted text-xs">
              {lang === 'ko' ? '잠시만 기다려주세요. 생성이 끝나면 자동으로 다운로드됩니다.' : 'Please wait. Your download will start automatically.'}
            </div>
          </div>
        </div>
      )}

      {/* PDF로 캡처될 영역 */}
      <div ref={reportRef} className="relative space-y-4 print:p-4" data-testid="result-report">
        {/* 데모 워터마크 - PDF 캡처 시 포함됨 */}
        {isDemo && (
          <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden print:block">
            <div 
              className="absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial Black, sans-serif' font-size='36' font-weight='900' fill='%23000' transform='rotate(-25 200 100)'%3EReleaseGate%3C/text%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat',
                backgroundSize: '400px 200px',
              }}
            />
            <div 
              className="absolute inset-0 opacity-[0.035] dark:opacity-[0.06]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial Black, sans-serif' font-size='36' font-weight='900' fill='%23fff' transform='rotate(-25 200 100)'%3EReleaseGate%3C/text%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat',
                backgroundSize: '400px 200px',
              }}
            />
          </div>
        )}
        
        {/* 리포트 헤더 */}
        <div className="card p-4" data-testid="result-header">
          <div className="flex items-center justify-between text-terminal-dim text-xs mb-4">
            <span>{t.result.reportTitle}</span>
            <span data-testid="result-timestamp">{now}</span>
          </div>
          
          <div className="text-terminal-dim text-xs mb-2">
            $ evaluate --feature "{result.feature_name}"
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="text-terminal-muted text-xs mb-1">{t.result.feature}</div>
              <div className="text-terminal-text text-lg font-semibold" data-testid="result-feature-name">{result.feature_name}</div>
            </div>
            
            <div>
              <div className="text-terminal-muted text-xs mb-1">{t.result.releaseDate}</div>
              <div className="text-terminal-text" data-testid="result-release-date">{result.release_date}</div>
            </div>
          </div>
        </div>

        {/* 점수 및 판정 */}
        <div className="card p-4" data-testid="result-evaluation">
          <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
            # {t.result.evaluationResult}
          </div>
          
          <div className="space-y-4">
            {/* 점수 */}
            <div className="flex items-end gap-4">
              <div>
                <div className="text-terminal-muted text-xs mb-1">{t.result.confidenceScore}</div>
                <div className={`text-5xl font-bold ${config.color}`} data-testid="result-confidence-score">
                  {result.confidence_score}
                  <span className="text-terminal-dim text-2xl">/100</span>
                </div>
              </div>
              <div className="pb-2">
                <span className={`text-2xl font-bold ${config.color}`} data-testid="result-grade">
                  {scoreGrade.grade}
                </span>
                <span className="text-terminal-muted text-sm ml-2">
                  ({scoreGrade.desc})
                </span>
              </div>
            </div>

            {/* 상태 */}
            <div className="pt-4 border-t border-terminal-border">
              <div className="flex items-center gap-3">
                <span className="text-terminal-muted text-sm">{t.result.status}:</span>
                <span className={`px-3 py-1 rounded text-sm font-bold ${config.badge}`} data-testid="result-status">
                  {config.label}
                </span>
                <span className={`text-sm ${config.color}`}>{config.desc}</span>
              </div>
            </div>

            {/* 판정 근거 */}
            <div className="pt-4 border-t border-terminal-border">
              <div className="text-terminal-dim text-xs mb-2">{t.result.judgmentBasis}</div>
              <ul className="text-sm text-terminal-muted space-y-1">
                {result.confidence_score >= 80 && (
                  <li>• {t.result.allItemsGood}</li>
                )}
                {result.confidence_score >= 60 && result.confidence_score < 80 && (
                  <>
                    <li>• {t.result.someImprovementNeeded}</li>
                    <li>• {t.result.reviewRecommendations}</li>
                  </>
                )}
                {result.confidence_score < 60 && (
                  <>
                    <li>• {t.result.multipleUnmet}</li>
                    <li>• {t.result.reEvaluationRecommended}</li>
                  </>
                )}
                {result.blockers.length > 0 && (
                  <li className="text-status-danger">
                    • {t.result.blockersToResolve.replace('{count}', String(result.blockers.length))}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* 입력 데이터 섹션 */}
        {inputData && (
          <div className="card p-4" data-testid="input-data-section">
            <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
              # {t.result.inputData}
            </div>
            
            <div className="space-y-4">
              {/* GitHub 커밋 분석(선택) */}
              {githubCtx?.commits?.length ? (
                <GitHubReportBlock
                  lang={lang}
                  githubCtx={githubCtx}
                  inputData={{
                    planning_intent: inputData.planning_intent,
                    risks: inputData.risks,
                    suggested_testcases: inputData.suggested_testcases ?? null,
                  }}
                />
              ) : null}

              {/* 첨부된 기획서 이미지 (MAX/Team 플랜 전용) */}
              {canUseAIReport && inputData.planning_files && inputData.planning_files.length > 0 && (() => {
                // 이미지 파일만 필터링
                const imageFiles = inputData.planning_files.filter(f => {
                  const ext = f.filename?.toLowerCase().split('.').pop();
                  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext || '');
                });
                
                if (imageFiles.length === 0) return null;
                
                return (
                  <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                    <div className="text-terminal-dim text-xs mb-2 flex items-center gap-2">
                      <span>🖼️</span>
                      <span>{lang === 'ko' ? '첨부된 기획 이미지' : 'Attached Planning Images'}</span>
                      <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                    </div>
                    <div className="grid gap-3">
                      {imageFiles.map((file, index) => (
                        <div key={index} className="relative group">
                          <div className="bg-terminal-dim/20 rounded-lg border border-terminal-border overflow-hidden">
                            <div className="p-2 bg-terminal-dim/30 border-b border-terminal-border">
                              <span className="text-xs text-terminal-muted truncate block">{file.filename}</span>
                            </div>
                            <div className="p-2 flex justify-center">
                              <img
                                src={`data:${file.content_type || 'image/png'};base64,${file.content}`}
                                alt={file.filename}
                                className="max-w-full max-h-64 object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => {
                                  // 새 창에서 이미지 열기
                                  const win = window.open();
                                  if (win) {
                                    win.document.write(`<img src="data:${file.content_type || 'image/png'};base64,${file.content}" style="max-width:100%;max-height:100%;" />`);
                                  }
                                }}
                                title={lang === 'ko' ? '클릭하여 크게 보기' : 'Click to view larger'}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* 기획 의도 */}
              {inputData.planning_intent.length > 0 && (
                <div>
                  <div className="text-terminal-dim text-xs mb-1">{t.form.planningIntent}</div>
                  <ul className="text-sm text-terminal-text space-y-1">
                    {inputData.planning_intent.map((intent, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-terminal-dim">•</span>
                        <span>{stripBullet(intent)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 리스크 */}
              {inputData.risks.length > 0 && (
                <div>
                  <div className="text-terminal-dim text-xs mb-1">{t.form.risks}</div>
                  <ul className="text-sm text-terminal-text space-y-1">
                    {inputData.risks.map((risk, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-status-warning">⚠</span>
                        <span>{stripBullet(risk)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* QA 상태 */}
              <div>
                <div className="text-terminal-dim text-xs mb-1">{t.form.qaStatus}</div>
                <div className="space-y-1">
                  {Object.entries(inputData.qa_status).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <span className="text-terminal-muted">{key}:</span>
                      <span className={`${
                        value.includes('검증 완료') || value.includes('Verified') 
                          ? 'text-status-go' 
                          : value.includes('미검증') || value.includes('Unverified')
                          ? 'text-status-danger'
                          : 'text-status-warning'
                      }`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI 제안 테스트 케이스 결과 */}
              {inputData.suggested_testcases && inputData.suggested_testcases.length > 0 && (
                <SuggestedTestcasesResult testcases={inputData.suggested_testcases} lang={lang} />
              )}
            </div>
          </div>
        )}

        {/* 상세 점수 */}
        {result.breakdown && result.breakdown.length > 0 && (
        <div className="card p-4" data-testid="result-breakdown">
          <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
            # {t.result.itemScores}
          </div>
          
          <div className="space-y-4" data-testid="result-breakdown-list">
            {result.breakdown.map((item) => {
              const pct = Math.round((item.score / item.max_score) * 100);
              // 카테고리 표시: 한국어면 category_ko, 영어면 category
              const categoryLabel = lang === 'ko' ? item.category_ko : item.category;
              return (
                <div key={item.category}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-terminal-muted">{categoryLabel}</span>
                    <span className="text-terminal-text">
                      {item.score}<span className="text-terminal-dim">/{item.max_score}</span>
                      <span className="text-terminal-dim text-xs ml-2">({pct}%)</span>
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className={`progress-fill ${getBarColor(item.score, item.max_score)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-terminal-dim text-xs mt-1">{item.feedback}</div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* 컨텍스트 인사이트 (플랫폼, 기술, 서비스, 도메인) */}
        {result.context_insights && result.context_insights.length > 0 && (
          <div className="card p-4" data-testid="result-context-insights">
            <div className="text-purple-400 text-xs mb-4 uppercase tracking-wide">
              # {t.result.platformInsights} <span className="text-terminal-dim normal-case">({t.result.platformInsightsSubtitle})</span>
            </div>
            <div className="space-y-4">
              {result.context_insights.map((insight: ContextInsight, i: number) => {
                const insightConfig = getInsightConfig(insight.insight_type, insight.keyword);
                const typeLabel = getInsightTypeLabel(insight.insight_type);
                return (
                  <div 
                    key={i} 
                    className={`border rounded-lg p-4 ${insightConfig.bgColor} ${insightConfig.borderColor}/30`}
                    data-testid={`context-insight-${insight.insight_type}-${i}`}
                  >
                    {/* 인사이트 헤더 */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-terminal-border/30 flex-wrap">
                      <span className="text-xl">{insightConfig.icon}</span>
                      <span className={`font-bold ${insightConfig.color}`}>{insight.keyword}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${insightConfig.bgColor} ${insightConfig.color} border ${insightConfig.borderColor}/50`}>
                        {typeLabel}
                      </span>
                      {insight.version_or_detail && (
                        <span className="text-xs px-2 py-0.5 bg-terminal-bg rounded-full text-terminal-muted">
                          {insight.version_or_detail}
                        </span>
                      )}
                    </div>
                    
                    {/* 주요 변경 사항 - 문자열만 렌더링 */}
                    {insight.key_changes.filter(item => typeof item === 'string').length > 0 && (
                      <div className="mb-3">
                        <div className="text-terminal-dim text-xs mb-1 uppercase">{t.result.keyChanges}</div>
                        <ul className="space-y-1">
                          {insight.key_changes.filter(item => typeof item === 'string').map((change, j) => (
                            <li key={j} className="text-sm text-terminal-text flex items-start gap-2">
                              <span className="text-terminal-dim">📋</span>
                              <span>{change}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* 충족된 점 - 문자열만 렌더링 */}
                    {insight.aligned_points.filter(item => typeof item === 'string').length > 0 && (
                      <div className="mb-3">
                        <div className="text-status-go text-xs mb-1 uppercase">{t.result.alignedPoints}</div>
                        <ul className="space-y-1">
                          {insight.aligned_points.filter(item => typeof item === 'string').map((point, j) => (
                            <li key={j} className="text-sm text-status-go flex items-start gap-2">
                              <span>✅</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* 확인 필요한 점 - 문자열만 렌더링 */}
                    {insight.gaps.filter(item => typeof item === 'string').length > 0 && (
                      <div className="mb-3">
                        <div className="text-status-warning text-xs mb-1 uppercase">{t.result.gaps}</div>
                        <ul className="space-y-1">
                          {insight.gaps.filter(item => typeof item === 'string').map((gap, j) => (
                            <li key={j} className="text-sm text-status-warning flex items-start gap-2">
                              <span>⚠️</span>
                              <span>{gap}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* 팁 - 문자열만 렌더링 */}
                    {insight.tips.filter(item => typeof item === 'string').length > 0 && (
                      <div>
                        <div className="text-blue-400 text-xs mb-1 uppercase">{t.result.tips}</div>
                        <ul className="space-y-1">
                          {insight.tips.filter(item => typeof item === 'string').map((tip, j) => (
                            <li key={j} className="text-sm text-blue-400 flex items-start gap-2">
                              <span>💡</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 블로커 */}
        {result.blockers.length > 0 && (
          <div className="card p-4 border-status-danger" data-testid="result-blockers">
            <div className="text-status-danger text-xs mb-3 uppercase tracking-wide">
              # {t.result.blockers} ({t.result.blockersSubtitle})
            </div>
            <ul className="space-y-2" data-testid="result-blockers-list">
              {result.blockers.map((blocker, i) => (
                <li key={i} className="text-sm text-status-danger flex items-start gap-2" data-testid={`blocker-item-${i}`}>
                  <span className="text-terminal-dim">[{i + 1}]</span>
                  <span>{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 구조화된 권고사항 */}
        {result.structured_recommendations && result.structured_recommendations.length > 0 ? (
          <div className="card p-4" data-testid="result-recommendations">
            <div className="text-status-warning text-xs mb-4 uppercase tracking-wide">
              # {t.result.recommendations}
            </div>
            <div className="space-y-4" data-testid="result-recommendations-list">
              {result.structured_recommendations.map((rec: StructuredRecommendation, i: number) => {
                const priorityConfig = getPriorityConfig(rec.priority);
                return (
                  <div 
                    key={i} 
                    className={`border rounded-lg p-4 ${priorityConfig.bg}`}
                    data-testid={`recommendation-item-${i}`}
                  >
                    {/* 헤더: 제목 + 우선순위 */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className={`font-semibold text-sm ${priorityConfig.color}`}>
                        {priorityConfig.icon} {rec.title}
                      </h4>
                      <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig.color} border border-current/30`}>
                        {priorityConfig.label}
                      </span>
                    </div>
                    
                    {/* 설명 */}
                    <p className="text-terminal-muted text-sm mb-3">
                      {rec.description}
                    </p>
                    
                    {/* 액션 아이템 */}
                    {rec.action_items.length > 0 && (
                      <div className="mb-3">
                        <div className="text-terminal-dim text-xs mb-1 uppercase">
                          📋 {t.result.actionItems}
                        </div>
                        <ul className="space-y-1">
                          {rec.action_items.map((item, j) => (
                            <li key={j} className="text-sm text-terminal-text flex items-start gap-2">
                              <span className="text-terminal-dim">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* 메타 정보: 마감일 & 담당자 */}
                    <div className="flex flex-wrap gap-4 text-xs text-terminal-dim pt-2 border-t border-terminal-border/50">
                      {rec.deadline && (
                        <div className="flex items-center gap-1">
                          <span>⏰</span>
                          <span className="text-terminal-muted">{t.result.deadline}:</span>
                          <span className="text-terminal-text">{rec.deadline}</span>
                        </div>
                      )}
                      {rec.owner && (
                        <div className="flex items-center gap-1">
                          <span>👤</span>
                          <span className="text-terminal-muted">{t.result.owner}:</span>
                          <span className="text-terminal-text">{rec.owner}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : result.recommendations.length > 0 && (
          /* 기존 단순 권고사항 (하위 호환) */
          <div className="card p-4" data-testid="result-recommendations">
            <div className="text-status-warning text-xs mb-3 uppercase tracking-wide">
              # {t.result.recommendations}
            </div>
            <ul className="space-y-2" data-testid="result-recommendations-list">
              {result.recommendations.map((rec, i) => (
                <li key={i} className="text-sm text-terminal-text flex items-start gap-2" data-testid={`recommendation-item-${i}`}>
                  <span className="text-status-warning">→</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 서명 영역 - Team 플랜만 표시 */}
        {isTeamPlan && (
          <div className="card p-4">
            <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
              # {t.result.confirmation}
            </div>
            <div className="grid grid-cols-2 gap-8 text-sm">
              <div>
                <div className="text-terminal-dim text-xs mb-1">{t.result.author}</div>
                <div className="border-b border-terminal-border pb-4 pt-2 min-h-[24px]">
                  &nbsp;
                </div>
              </div>
              <div>
                <div className="text-terminal-dim text-xs mb-1">{t.result.approver}</div>
                <div className="border-b border-terminal-border pb-4 pt-2 min-h-[24px]">
                  &nbsp;
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF 에러 메시지 */}
      {pdfError && (
        <div className="bg-status-danger/10 border border-status-danger/30 text-status-danger text-sm p-3 rounded-lg print:hidden">
          {pdfError}
        </div>
      )}

      {/* 버튼들 (인쇄 시 숨김) */}
      <div className="flex flex-col gap-3 print:hidden">
        {/* MAX/Team 플랜: AI 리포트 버튼 */}
        {user && canUseAIReport && (
          <button
            onClick={() => {
              analytics.maxReportView();
              setShowMaxReport(true);
            }}
            className="w-full py-3 rounded text-sm bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/50 text-purple-400 hover:from-purple-500/30 hover:to-blue-500/30 transition-all flex items-center justify-center gap-2"
          >
            <span>✨</span>
            {t.result.maxAiReport}
            <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-300 text-xs font-semibold rounded">MAX</span>
          </button>
        )}
        
        {/* Pro 플랜: AI 리포트 잠금 버튼 */}
        {user && isProPlan && !canUseAIReport && (
          <div 
            className="w-full py-3 rounded text-sm bg-terminal-dim/20 border border-terminal-border text-terminal-muted cursor-not-allowed flex items-center justify-center gap-2 relative group"
            title={lang === 'ko' ? 'MAX 플랜에서 사용 가능' : 'Available for MAX plan'}
          >
            <span>🔒</span>
            {t.result.maxAiReport}
            <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
            {/* 업그레이드 안내 툴팁 */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-terminal-bg border border-terminal-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              <div className="text-xs text-terminal-muted">
                ⭐ {lang === 'ko' ? 'MAX 플랜 전용' : 'MAX Plan Only'}
              </div>
              <div className="text-xs text-purple-400 mt-1">
                {t.result.maxUpgradeHint}
              </div>
            </div>
          </div>
        )}
        
        {/* PDF 다운로드 버튼 영역 - z-20으로 워터마크(z-10) 위에 표시 */}
        <div className="flex gap-3 relative z-20">
          {/* Pro 이상: 서버사이드 PDF */}
          {user && canDownloadPdf ? (
            <button
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              className="flex-1 py-3 rounded text-sm border border-status-go text-status-go bg-terminal-bg hover:bg-status-go/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="button-save-pdf"
            >
              {isGenerating ? t.result.pdfDownloading : t.result.pdfDownload}
            </button>
          ) : (
            /* Free 플랜 또는 비로그인: 클라이언트 PDF + 업그레이드 안내 */
            <div className="flex-1 relative group">
              <button
                onClick={handleDownloadPDFClient}
                disabled={isGenerating}
                className="w-full py-3 rounded text-sm border border-terminal-border text-terminal-muted bg-terminal-bg hover:border-terminal-text hover:text-terminal-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="button-save-pdf"
              >
                {isGenerating ? t.result.generating : t.result.savePdf}
              </button>
              {/* Pro 업그레이드 안내 툴팁 */}
              {user && !canDownloadPdf && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-terminal-bg border border-terminal-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-30">
                  <div className="text-xs text-terminal-muted">
                    ⭐ {t.result.pdfProOnly}
                  </div>
                  <div className="text-xs text-status-go mt-1">
                    {t.result.pdfUpgradeHint}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={() => {
              analytics.resetClick();
              onReset();
            }}
            className="flex-1 py-3 rounded text-sm border border-terminal-border text-terminal-muted bg-terminal-bg hover:border-terminal-text hover:text-terminal-text transition-colors"
            data-testid="button-new-evaluation"
          >
            {t.result.newEvaluation}
          </button>
        </div>
        
        {/* MAX 플랜 안내 배너 (Pro 플랜 사용자, Max/Team 아닌 경우) - z-20으로 워터마크 위에 표시 */}
        {user && canDownloadPdf && !canUseAIReport && (
          <div className="relative z-20 bg-gradient-to-r from-purple-500/5 to-blue-500/5 border border-purple-500/20 rounded-lg p-3 text-center">
            <div className="text-xs text-terminal-muted">
              ✨ {t.result.maxUpgradeHint}
            </div>
          </div>
        )}
        
        {/* Pro 플랜 안내 배너 (비로그인 또는 Free 플랜) - z-20으로 워터마크 위에 표시 */}
        {(!user || !canDownloadPdf) && (
          <div className="relative z-20 bg-gradient-to-r from-status-go/5 to-status-warning/5 border border-status-go/20 rounded-lg p-3 text-center">
            <div className="text-xs text-terminal-muted">
              ✨ Pro 플랜에서는 <span className="text-status-go font-semibold">전문 PDF 리포트</span>를 다운로드할 수 있습니다
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultBoard;
